import React from 'react';
import { Link } from 'react-router-dom';
import { useMemberDetail } from '../state/MemberDetailContext';
import '../member-details.css';

interface MemberNameProps {
  teamId: string;
  /** Team member: clicking opens the member detail modal */
  userId?: number | null;
  /** Agent actor: clicking navigates to the agent's page instead */
  agentId?: number | null;
  children: React.ReactNode;
  className?: string;
}

/**
 * The single click-target for a person's name anywhere in the UI: members
 * open the member detail modal, agents link to their detail page, and
 * anything unidentifiable renders as plain text.
 */
const MemberName: React.FC<MemberNameProps> = ({
  teamId,
  userId,
  agentId,
  children,
  className,
}) => {
  const { openMember } = useMemberDetail();

  if (userId) {
    // A real link: keyboard-focusable and cmd/ctrl/middle-click opens the
    // team members page in a new tab, while a plain click opens the member
    // detail modal in place.
    return (
      <a
        href={`/console/team/${teamId}/users`}
        className={`member-name-link ${className || ''}`.trim()}
        data-testid={`member-name-${userId}`}
        onClick={(event) => {
          if (
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return; // let the browser open the members page (new tab, etc.)
          }
          event.preventDefault();
          event.stopPropagation();
          openMember(teamId, userId);
        }}
        title="View member details">
        {children}
      </a>
    );
  }

  if (agentId) {
    return (
      <Link
        to={`/console/team/${teamId}/agent/${agentId}`}
        className={`member-name-link ${className || ''}`.trim()}
        onClick={(event) => event.stopPropagation()}
        title="View agent details">
        {children}
      </Link>
    );
  }

  return <span className={className}>{children}</span>;
};

export default MemberName;
